import { ContentSkeleton } from '@/components/loading/Skeleton'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '@/util/api'
import { usePublicEventViewerUpdates } from '@/hooks/usePublicEventViewerUpdates'
import {
  loadViewerPayload,
  type ViewerCamera,
  type ViewerDestinationSnapshot,
  type ViewerPayload,
} from '@/lib/comrades/viewerPayload'
import type {
  Layout,
  LayoutCamera,
  LayoutContextValue,
  LayoutEvent,
} from '../layout'
import { TEMPLATE_REGISTRY, lookupTemplate } from '../templates/registry'
import { DestinationRuntime } from './DestinationRuntime'
import { useDestinationProviderValues } from './providerSnapshots'
import { validateDestinationLayout } from './manifestValidation'
import type {
  CameraSelection,
  DestinationCamera,
  DestinationEvent,
  DestinationLiveStateSnapshot,
  DestinationMode,
  DestinationViewerSession,
  JsonValue,
} from './types'
import '../primitives/shell/registerAll'
import '../shells/le-vanilla/LeVanillaShell'
import '../shells/le-vanilla-map/LeVanillaMapShell'

interface DestinationDraftResponse {
  event_id: string
  layout: Record<string, unknown>
  link_to_template: boolean
  base_layout_revision: number
  draft_revision: number
  updated_at: string | null
}

export interface DestinationPreviewMetadata {
  publishedRevision: number
  draftRevision: number
  linkedToTemplate: boolean
}

export interface DestinationPreviewProps {
  eventId: string
  mode?: Extract<DestinationMode, 'preview' | 'studio'>
  refreshKey?: number
  onLoaded?: (metadata: DestinationPreviewMetadata) => void
  onError?: (message: string) => void
}

function previewCameraIsOnline(camera: ViewerCamera): boolean {
  return ['active', 'live', 'online'].includes(camera.status.toLowerCase())
}

function toLayoutCamera(camera: ViewerCamera): LayoutCamera {
  return {
    participant_identity: camera.participant_identity,
    name: camera.name,
    position_km: camera.location?.distance_km ?? null,
    online: previewCameraIsOnline(camera),
    public_audio_enabled: camera.public_audio_enabled !== false,
    preview_image_url: camera.preview_image_url ?? null,
    latitude: camera.location?.latitude ?? null,
    longitude: camera.location?.longitude ?? null,
  }
}

function toDestinationCamera(camera: ViewerCamera): DestinationCamera {
  return {
    id: camera.id ?? camera.stream_id,
    identity: camera.participant_identity,
    name: camera.name,
    positionKm: camera.location?.distance_km ?? null,
    online: previewCameraIsOnline(camera),
    publicAudioEnabled: camera.public_audio_enabled !== false,
    previewImageUrl: camera.preview_image_url ?? null,
    coordinate:
      camera.location
        ? {
            latitude: camera.location.latitude,
            longitude: camera.location.longitude,
          }
        : null,
    media: {
      identity: camera.participant_identity,
      revision: 0,
    },
  }
}

function toLayoutEvent(payload: ViewerPayload, templateKey: string): LayoutEvent {
  const { event, route } = payload
  const organization = event.organization

  return {
    id: event.id,
    name: event.name,
    subtitle: event.subtitle ?? null,
    description: event.description ?? null,
    starts_at: event.starts_at ?? null,
    ends_at: event.ends_at ?? null,
    template_key: templateKey,
    featured_participant_identity: payload.featured_participant_identity,
    organization: organization
      ? {
          id: organization.id,
          name: organization.name,
          display_name: organization.display_name,
          description: organization.description,
          logo_url: organization.logo_url,
          verified: organization.verified,
          events_count: organization.events_count ?? 0,
        }
      : {
          id: 'streambridge',
          name: 'StreamBridge',
          display_name: null,
          description: null,
          logo_url: null,
          verified: false,
          events_count: 0,
        },
    route: route
      ? {
          id: route.id ?? null,
          distance_km: null,
          elevation_gain_m: null,
          elevation_loss_m: null,
          waypoint_count: null,
          centroid_lat: null,
          centroid_lng: null,
          bounds: route.bounds ?? null,
          gpx_url: route.gpx_url ?? null,
        }
      : null,
  }
}

function toDestinationEvent(event: LayoutEvent, status: string): DestinationEvent {
  return {
    id: event.id,
    publicId: event.id,
    name: event.name,
    subtitle: event.subtitle,
    description: event.description,
    status,
    startsAt: event.starts_at,
    endsAt: event.ends_at,
    templateKey: event.template_key,
    organization: {
      id: event.organization.id,
      name: event.organization.name,
      displayName: event.organization.display_name,
      description: event.organization.description,
      logoUrl: event.organization.logo_url,
      verified: event.organization.verified,
      eventsCount: event.organization.events_count,
    },
    route: event.route
      ? {
          id: event.route.id,
          distanceKm: event.route.distance_km,
          elevationGainM: event.route.elevation_gain_m,
          elevationLossM: event.route.elevation_loss_m,
          waypointCount: event.route.waypoint_count,
          centroid:
            event.route.centroid_lat != null && event.route.centroid_lng != null
              ? {
                  latitude: event.route.centroid_lat,
                  longitude: event.route.centroid_lng,
                }
              : null,
          bounds: event.route.bounds,
          gpxUrl: event.route.gpx_url,
        }
      : null,
  }
}

function toLiveState(
  destination: ViewerDestinationSnapshot,
): DestinationLiveStateSnapshot {
  return {
    revision: destination.live_state.revision,
    components: Object.fromEntries(
      Object.entries(destination.live_state.components).map(
        ([instanceId, component]) => [
          instanceId,
          {
            state: component.state as JsonValue,
            revision: component.revision,
            updatedAt: component.updated_at,
            expiresAt: component.expires_at,
          },
        ],
      ),
    ),
  }
}

const directorSession: DestinationViewerSession = {
  id: 'director-preview',
  kind: 'authenticated',
  roles: ['director'],
  capabilities: ['destination:edit', 'destination:operate'],
  preferences: {},
}

function PreviewSkeleton() {
  return (
    <div
      className="flex min-h-full items-center justify-center bg-neutral-1 text-sm text-neutral-7"
      aria-busy="true"
      aria-label="Loading destination preview"
    >
      <ContentSkeleton label="Loading destination preview" variant="viewer" className="p-6" />
    </div>
  )
}

export function DestinationPreview({
  eventId,
  mode = 'preview',
  refreshKey = 0,
  onLoaded,
  onError,
}: DestinationPreviewProps) {
  const [payload, setPayload] = useState<ViewerPayload | null>(null)
  const [destination, setDestination] = useState<ViewerDestinationSnapshot | null>(null)
  const [draft, setDraft] = useState<DestinationDraftResponse | null>(null)
  const [selectedIdentity, setSelectedIdentity] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setError(null)

    Promise.all([
      loadViewerPayload(eventId),
      api.get<{ data: ViewerDestinationSnapshot }>(
        `/events/${eventId}/destination`,
      ),
      api.get<{ data: DestinationDraftResponse }>(
        `/events/${eventId}/destination/draft`,
      ),
    ])
      .then(([viewerPayload, destinationResponse, draftResponse]) => {
        if (cancelled) return
        setPayload(viewerPayload)
        setDestination(destinationResponse.data)
        setDraft(draftResponse.data)
        onLoaded?.({
          publishedRevision: destinationResponse.data.layout_revision,
          draftRevision: draftResponse.data.draft_revision,
          linkedToTemplate: draftResponse.data.link_to_template,
        })
      })
      .catch((reason: unknown) => {
        if (cancelled) return
        const message =
          reason instanceof Error ? reason.message : 'Failed to load destination preview'
        setError(message)
        onError?.(message)
      })

    return () => {
      cancelled = true
    }
  }, [eventId, onError, onLoaded, refreshKey])

  usePublicEventViewerUpdates({
    eventId: payload?.event.id,
    payload,
    setPayload,
  })

  const runtimeDestination = payload?.destination ?? destination
  const providerValues = useDestinationProviderValues(
    eventId,
    runtimeDestination ?? undefined,
  )

  const cameras = useMemo(
    () => payload?.cameras.map(toDestinationCamera) ?? [],
    [payload],
  )
  const producerFeaturedIdentity =
    payload?.featured_participant_identity ?? null
  const selectedCameraIsOnline = cameras.some(
    (camera) => camera.identity === selectedIdentity && camera.online,
  )
  const featuredCameraIsOnline = cameras.some(
    (camera) =>
      camera.identity === producerFeaturedIdentity && camera.online,
  )
  const watchingIdentity =
    (selectedCameraIsOnline ? selectedIdentity : null) ??
    (featuredCameraIsOnline ? producerFeaturedIdentity : null) ??
    cameras.find((camera) => camera.online)?.identity ??
    null

  const selectCamera = useCallback((identity: string) => {
    setSelectedIdentity(identity)
  }, [])
  const followProducer = useCallback(() => setSelectedIdentity(null), [])
  const cameraSelection = useMemo<CameraSelection>(
    () => ({
      selectedIdentity: watchingIdentity,
      producerFeaturedIdentity,
      hasViewerOverride: selectedCameraIsOnline,
      select: selectCamera,
      followProducer,
    }),
    [
      followProducer,
      producerFeaturedIdentity,
      selectCamera,
      selectedCameraIsOnline,
      watchingIdentity,
    ],
  )

  if (error) {
    return (
      <div className="flex min-h-full items-center justify-center bg-neutral-1 p-6 text-sm text-error">
        {error}
      </div>
    )
  }
  if (!payload || !runtimeDestination || !draft) return <PreviewSkeleton />

  const templateKey =
    payload.event.template_key &&
    TEMPLATE_REGISTRY[payload.event.template_key]
      ? payload.event.template_key
      : 'le_vanilla'
  const template =
    lookupTemplate(templateKey) ?? TEMPLATE_REGISTRY.le_vanilla
  const validatedDraft = validateDestinationLayout(draft.layout)
  const validatedPublished = validateDestinationLayout(runtimeDestination.layout)
  const layout: Layout = validatedDraft.ok
    ? validatedDraft.value
    : validatedPublished.ok
      ? validatedPublished.value
      : template.defaultLayout
  const layoutEvent = toLayoutEvent(payload, templateKey)
  const layoutCameras = payload.cameras.map(toLayoutCamera)
  const legacyLayoutContext: LayoutContextValue = {
    event: layoutEvent,
    cameras: layoutCameras,
    room: null,
    viewerCount: null,
    featuredFromRoom: producerFeaturedIdentity,
    selection: {
      watchingIdentity,
      hasOverride: selectedCameraIsOnline,
      selectCamera,
      followProducer,
    },
  }

  return (
    <DestinationRuntime
      mode={mode}
      layout={layout}
      layoutRevision={draft.draft_revision}
      event={toDestinationEvent(layoutEvent, payload.event.status)}
      cameras={cameras}
      cameraSelection={cameraSelection}
      viewerCount={null}
      liveState={toLiveState(runtimeDestination)}
      viewerSession={directorSession}
      tracking={providerValues.tracking}
      results={providerValues.results}
      legacyLayoutContext={legacyLayoutContext}
    />
  )
}
